import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type DateFilter = 'today' | 'lastWeek' | 'lastMonth' | 'last3months' | 'last6months' | 'lastYear' | 'custom'

interface FilterState {
  // Aktualny symbol
  selectedSymbol: string
  setSelectedSymbol: (symbol: string) => void
  
  // Aktualny filtr daty
  dateFilter: DateFilter
  setDateFilter: (filter: DateFilter) => void
  
  // Custom date range (dla dateFilter === 'custom')
  customDateFrom: string // format DD.MM
  customDateTo: string   // format DD.MM
  setCustomDateRange: (from: string, to: string) => void
  
  // Resetowanie filtrów
  resetFilters: () => void
  resetDateFilter: () => void
  
  // Helper do pobierania pełnej konfiguracji filtra
  getFilterConfig: () => {
    symbol: string
    dateFilter: DateFilter
    customFrom?: string
    customTo?: string
  }
}

const initialState = {
  selectedSymbol: 'allsymbols',
  dateFilter: 'lastYear' as DateFilter,
  customDateFrom: '',
  customDateTo: '',
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      setSelectedSymbol: (symbol) => set({ selectedSymbol: symbol }),
      
      setDateFilter: (filter) => set({ dateFilter: filter }),
      
      setCustomDateRange: (from, to) => set({ 
        customDateFrom: from, 
        customDateTo: to 
      }),
      
      resetFilters: () => set(initialState),
      
      resetDateFilter: () => set({
        dateFilter: 'lastYear',
        customDateFrom: '',
        customDateTo: ''
      }),
      
      getFilterConfig: () => {
        const state = get()
        return {
          symbol: state.selectedSymbol,
          dateFilter: state.dateFilter,
          customFrom: state.dateFilter === 'custom' ? state.customDateFrom : undefined,
          customTo: state.dateFilter === 'custom' ? state.customDateTo : undefined
        }
      }
    }),
    {
      name: 'filter-storage',
      version: 2,
      migrate: (persisted: unknown, version: number) => {
        const merged = {
          ...initialState,
          ...(typeof persisted === 'object' && persisted !== null
            ? (persisted as Record<string, unknown>)
            : {}),
        } as typeof initialState & Record<string, unknown>
        if (version < 2) {
          merged.dateFilter = 'lastYear' as DateFilter
        }
        return merged
      },
      // Persistuj wszystkie filtry
      partialize: (state) => ({
        selectedSymbol: state.selectedSymbol,
        dateFilter: state.dateFilter,
        customDateFrom: state.customDateFrom,
        customDateTo: state.customDateTo
      })
    }
  )
)
// Selektory dla optymalizacji re-renderów
export const useSelectedSymbol = () => useFilterStore((state) => state.selectedSymbol)
export const useDateFilter = () => useFilterStore((state) => state.dateFilter)
export const useCustomDateRange = () => useFilterStore((state) => ({
  from: state.customDateFrom,
  to: state.customDateTo
}))

// Labele dla UI
export const DATE_FILTER_LABELS: Record<DateFilter, string> = {
  today: 'Today',
  lastWeek: 'Last Week',
  lastMonth: 'Last Month',
  last3months: 'Last 3 Months',
  last6months: 'Last 6 Months',
  lastYear: 'Last Year',
  custom: 'Custom Range'
}

// Opcje dla selecta/dropdowna
export const DATE_FILTER_OPTIONS: Array<{ value: DateFilter; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'last3months', label: 'Last 3 Months' },
  { value: 'last6months', label: 'Last 6 Months' },
  { value: 'lastYear', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' }
]